import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment,
} from "@mui/material";
import * as Yup from "yup";
import Images from "../../Images";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";
import { setUser, setRegistrationData } from "../../redux/UserSlice";
import { registerUser } from "../../requests/auth/Authentication";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import GuestRequests from "../../requests/GuestRequests";
import { regionOptions, locationsByRegion } from "../../utils/data";

import Loader from "../../components/Loader";
import { useState, useEffect, useRef } from "react";
import {
  UserCircle2,
  Building2,
  ClipboardCheck,
  CircleCheckBig,
  Globe,
  Instagram,
  Facebook,
  Eye,
  EyeOff,
} from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// * Request Validation
const validationSchema = Yup.object().shape({
  step1: Yup.object({
    brandName: Yup.string().required("Company Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .min(12, "Phone number is too short")
      .max(12, "Phone number is too long"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_])[A-Za-z\d!@#$%^&*(),.?":{}|<>_]{8,}$/,
        'Password must contain at least one uppercase letter, one number, and one special character (!@#$%^&*(),.?":{}|<>_)'
      ),
    confirmPassword: Yup.string()
      .required("Confirm Password is required")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
    preferredCategory: Yup.string().required("Industry is required"),
    customCategory: Yup.string().when("preferredCategory", {
      is: "Other",
      then: () =>
        Yup.string()
          .required("Please specify your industry")
          .min(3, "Industry name must be at least 3 characters")
          .matches(
            /^[a-zA-Z\s&-]+$/,
            "Industry name can only contain letters, spaces, & and -"
          ),
      otherwise: () => Yup.string(),
    }),
  }),
  step2: Yup.object({
    region: Yup.string().required("Region is required"),
    location: Yup.string().required("City is required"),
    customLocation: Yup.string().when("location", {
      is: "Other",
      then: () => Yup.string().required("Please enter your city name"),
      otherwise: () => Yup.string(),
    }),
    logo: Yup.mixed(),
    websiteLink: Yup.string().url("Please enter a valid URL"),
    instagramLink: Yup.string(),
    twitterLink: Yup.string(),
    facebookLink: Yup.string(),
  }),
});

const initialValues = {
  step1: {
    brandName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    preferredCategory: "",
    customCategory: "",
  },
  step2: {
    region: "",
    location: "",
    customLocation: "",
    logo: null,
    websiteLink: "",
    instagramLink: "",
    twitterLink: "",
    facebookLink: "",
  },
};
const StepIndicator = ({ currentStep }) => {
  const steps = [
    {
      number: 1,
      label: "Account Details",
      icon: <UserCircle2 className="w-8 h-8" />,
    },
    {
      number: 2,
      label: "Business Info",
      icon: <Building2 className="w-8 h-8" />,
    },
    {
      number: 3,
      label: "Review",
      icon: <ClipboardCheck className="w-8 h-8" />,
    },
  ];

  return (
    <div className="mb-12">
      {/* Main Container */}
      <div className="relative">
        {/* Progress Bar Background */}
        <div className="absolute top-[2.5rem] left-[10%] w-[80%] h-2 bg-gray-100 rounded-full" />

        {/* Animated Progress Bar */}
        <motion.div
          className="absolute top-[2.5rem] left-[10%] h-2 rounded-full bg-gradient-to-r from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
          initial={{ width: "0%" }}
          animate={{
            width: currentStep === 1 ? "0%" : currentStep === 2 ? "40%" : "80%",
          }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
        />

        {/* Steps Container */}
        <div className="flex relative justify-between">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className="flex relative flex-col items-center"
            >
              {/* Step Circle */}
              <motion.div
                className={`relative flex items-center justify-center w-20 h-20 
                  ${
                    currentStep >= step.number
                      ? "bg-gradient-to-br from-[#FF6C6C] to-[#FF9B9B]"
                      : "bg-white"
                  } rounded-full shadow-lg border-4 border-white`}
                initial={false}
                animate={{
                  scale: currentStep === step.number ? 1.1 : 1,
                  y: currentStep === step.number ? -5 : 0,
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
              >
                {/* Step Content */}
                <motion.div
                  className="flex flex-col justify-center items-center"
                  initial={false}
                  animate={{
                    scale: currentStep === step.number ? 1.1 : 1,
                  }}
                >
                  {/* Icon */}
                  <motion.div
                    className={`${
                      currentStep >= step.number
                        ? "text-white"
                        : "text-gray-400"
                    }`}
                    initial={false}
                    animate={{
                      rotateY: currentStep >= step.number ? 360 : 0,
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    {currentStep > step.number ? (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <CircleCheckBig className="w-8 h-8" />
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        {step.icon}
                      </motion.div>
                    )}
                  </motion.div>
                </motion.div>

                {currentStep === step.number && (
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.5, 0] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    style={{
                      border: "3px solid rgba(255, 108, 108, 0.5)",
                      scale: 1.1,
                    }}
                  />
                )}
              </motion.div>

              {/* Step Label */}
              <motion.div
                className="mt-4 text-center"
                initial={false}
                animate={{
                  scale: currentStep === step.number ? 1.05 : 1,
                }}
              >
                <motion.p
                  className={`font-medium text-sm
                    ${
                      currentStep === step.number
                        ? "text-[#FF6C6C]"
                        : "text-gray-500"
                    }`}
                  initial={false}
                  animate={{
                    y: currentStep === step.number ? -2 : 0,
                  }}
                >
                  {step.label}
                </motion.p>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-5 h-5 text-gray-400"
    fill="currentColor"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Add this helper function at the top level
const truncateUrl = (url, maxLength = 30) => {
  if (!url) return "";
  if (url.length <= maxLength) return url;

  // Split the URL into parts
  const urlObj = new URL(url);
  const domain = urlObj.hostname;
  const path = urlObj.pathname + urlObj.search;

  // If domain itself is too long
  if (domain.length > maxLength - 3) {
    return domain.substring(0, maxLength - 3) + "...";
  }

  // If we need to truncate the path
  const availableLength = maxLength - domain.length - 3;
  const truncatedPath = path.substring(0, availableLength) + "...";
  return domain + truncatedPath;
};

const ReviewStep = ({ formData, onBack, onSubmit, isLoading }) => (
  <motion.div
    initial={{ opacity: 0, x: -50 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 50 }}
    transition={{ duration: 0.5 }}
    className="space-y-8"
  >
    <div className="space-y-6">
      {/* Brand Details Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="p-6 bg-white rounded-xl shadow-md transition-shadow hover:shadow-lg"
      >
        <div className="flex items-center mb-4 space-x-3">
          <UserCircle2 className="w-6 h-6 text-[#FF6C6C]" />
          <h3 className="text-lg font-semibold text-gray-800">Brand Details</h3>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="p-4 space-y-1 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-500">Company Name</p>
            <p className="text-base font-semibold text-gray-800">
              {formData.step1.brandName}
            </p>
          </div>
          <div className="p-4 space-y-1 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-500">Category</p>
            <p className="text-base font-semibold text-gray-800">
              {formData.step1.preferredCategory === "Other"
                ? formData.step1.customCategory
                : formData.step1.preferredCategory}
            </p>
          </div>
          <div className="p-4 space-y-1 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-500">Email</p>
            <p className="text-base font-semibold text-gray-800">
              {formData.step1.email}
            </p>
          </div>
          <div className="p-4 space-y-1 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-500">Phone</p>
            <p className="text-base font-semibold text-gray-800">
              {formData.step1.phoneNumber}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Location Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="p-6 bg-white rounded-xl shadow-md transition-shadow hover:shadow-lg"
      >
        <div className="flex items-center mb-4 space-x-3">
          <Building2 className="w-6 h-6 text-[#FF6C6C]" />
          <h3 className="text-lg font-semibold text-gray-800">Location</h3>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="p-4 space-y-1 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-500">Region</p>
            <p className="text-base font-semibold text-gray-800">
              {formData.step2.region}
            </p>
          </div>
          <div className="p-4 space-y-1 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-500">City</p>
            <p className="text-base font-semibold text-gray-800">
              {formData.step2.location === "Other"
                ? formData.step2.customLocation
                : formData.step2.location}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Online Presence Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="p-6 bg-white rounded-xl shadow-md transition-shadow hover:shadow-lg"
      >
        <div className="flex items-center mb-4 space-x-3">
          <Globe className="w-6 h-6 text-[#FF6C6C]" />
          <h3 className="text-lg font-semibold text-gray-800">
            Online Presence
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {formData.step2.websiteLink && (
            <div className="flex items-center p-4 space-x-3 bg-gray-50 rounded-lg">
              <Globe className="w-5 h-5 text-gray-400 shrink-0" />
              <div className="space-y-1 min-w-0">
                <p className="text-sm font-medium text-gray-500">Website</p>
                <p
                  className="text-base font-semibold text-gray-800 truncate"
                  title={formData.step2.websiteLink}
                >
                  {truncateUrl(formData.step2.websiteLink)}
                </p>
              </div>
            </div>
          )}
          {formData.step2.instagramLink && (
            <div className="flex items-center p-4 space-x-3 bg-gray-50 rounded-lg">
              <Instagram className="w-5 h-5 text-gray-400 shrink-0" />
              <div className="space-y-1 min-w-0">
                <p className="text-sm font-medium text-gray-500">Instagram</p>
                <p
                  className="text-base font-semibold text-gray-800 truncate"
                  title={formData.step2.instagramLink}
                >
                  {truncateUrl(formData.step2.instagramLink)}
                </p>
              </div>
            </div>
          )}
          {formData.step2.facebookLink && (
            <div className="flex items-center p-4 space-x-3 bg-gray-50 rounded-lg">
              <Facebook className="w-5 h-5 text-gray-400 shrink-0" />
              <div className="space-y-1 min-w-0">
                <p className="text-sm font-medium text-gray-500">Facebook</p>
                <p
                  className="text-base font-semibold text-gray-800 truncate"
                  title={formData.step2.facebookLink}
                >
                  {truncateUrl(formData.step2.facebookLink)}
                </p>
              </div>
            </div>
          )}
          {formData.step2.twitterLink && (
            <div className="flex items-center p-4 space-x-3 bg-gray-50 rounded-lg">
              <XIcon />
              <div className="space-y-1 min-w-0">
                <p className="text-sm font-medium text-gray-500">Twitter</p>
                <p
                  className="text-base font-semibold text-gray-800 truncate"
                  title={formData.step2.twitterLink}
                >
                  {truncateUrl(formData.step2.twitterLink)}
                </p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Logo Preview Section */}
      {formData.step2.logo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-6 bg-white rounded-xl shadow-md transition-shadow hover:shadow-lg"
        >
          <div className="flex items-center mb-4 space-x-3">
            <img
              src={URL.createObjectURL(formData.step2.logo)}
              alt="Brand Logo"
              className="object-cover w-8 h-8 rounded-full"
            />
            <h3 className="text-lg font-semibold text-gray-800">Brand Logo</h3>
          </div>
          <div className="overflow-hidden relative w-32 h-32 rounded-xl shadow-lg">
            <img
              src={URL.createObjectURL(formData.step2.logo)}
              alt="Logo preview"
              className="object-cover w-full h-full"
            />
          </div>
        </motion.div>
      )}
    </div>

    {/* Action Buttons */}
    <div className="flex space-x-4">
      <motion.button
        type="button"
        onClick={onBack}
        className="px-6 py-4 w-1/2 font-semibold text-gray-700 bg-gray-200 rounded-xl transition-all duration-200 hover:bg-gray-300"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        Back
      </motion.button>

      <motion.button
        type="button"
        onClick={onSubmit}
        disabled={isLoading}
        className="w-1/2 bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white font-semibold py-4 px-6 rounded-xl disabled:opacity-50"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {isLoading ? (
          <div className="flex justify-center items-center space-x-2">
            <div className="w-5 h-5 rounded-full border-t-2 border-b-2 border-white animate-spin" />
            <span>Creating Account...</span>
          </div>
        ) : (
          "Create Account"
        )}
      </motion.button>
    </div>
  </motion.div>
);

const PasswordStrengthIndicator = ({ password }) => {
  const [strength, setStrength] = useState({
    score: 0,
    hasLength: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecial: false,
  });

  useEffect(() => {
    const checks = {
      hasLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>_]/.test(password),
    };

    const score = Object.values(checks).filter(Boolean).length;
    setStrength({ ...checks, score });
  }, [password]);

  const getColor = (isValid) => (isValid ? "text-green-500" : "text-gray-300");

  return (
    <div className="mt-2 space-y-2">
      <div className="flex space-x-2">
        {[1, 2, 3, 4].map((index) => (
          <div
            key={index}
            className={`h-2 w-full rounded-full transition-all duration-300 ${
              index <= strength.score
                ? "bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B]"
                : "bg-gray-200"
            }`}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div
          className={`flex items-center space-x-1 ${getColor(
            strength.hasLength
          )}`}
        >
          <svg
            className={`w-4 h-4 ${
              strength.hasLength ? "text-green-500" : "text-gray-300"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          <span>8+ characters</span>
        </div>
        <div
          className={`flex items-center space-x-1 ${getColor(
            strength.hasUpperCase
          )}`}
        >
          <svg
            className={`w-4 h-4 ${
              strength.hasUpperCase ? "text-green-500" : "text-gray-300"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          <span>Uppercase letter</span>
        </div>
        <div
          className={`flex items-center space-x-1 ${getColor(
            strength.hasNumber
          )}`}
        >
          <svg
            className={`w-4 h-4 ${
              strength.hasNumber ? "text-green-500" : "text-gray-300"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          <span>Number</span>
        </div>
        <div
          className={`flex items-center space-x-1 ${getColor(
            strength.hasSpecial
          )}`}
        >
          <svg
            className={`w-4 h-4 ${
              strength.hasSpecial ? "text-green-500" : "text-gray-300"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          <span>Special character (!@#$%^&*_)</span>
        </div>
      </div>
    </div>
  );
};

const PasswordMatchIndicator = ({ password, confirmPassword }) => {
  const doPasswordsMatch = password === confirmPassword && password.length > 0;

  return (
    <div className="flex items-center mt-2 space-x-1">
      <svg
        className={`w-4 h-4 ${
          doPasswordsMatch ? "text-green-500" : "text-gray-300"
        }`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
      <span
        className={`text-sm ${
          doPasswordsMatch ? "text-green-500" : "text-gray-400"
        }`}
      >
        {doPasswordsMatch ? "Passwords match" : "Passwords must match"}
      </span>
    </div>
  );
};

// Map backend field names to Formik field paths
const mapBackendFieldToFormik = (backendField) => {
  const fieldMap = {
    brand_name: "step1.brandName",
    email: "step1.email",
    phone_number: "step1.phoneNumber",
    password: "step1.password",
    password_confirmation: "step1.confirmPassword",
    brand_category: "step1.preferredCategory",
    region: "step2.region",
    location: "step2.location",
    website: "step2.websiteLink",
    instagram: "step2.instagramLink",
    twitter: "step2.twitterLink",
    facebook: "step2.facebookLink",
    logo: "step2.logo",
  };

  return fieldMap[backendField] || null;
};

const handleServerErrors = (error, formik = null, setCurrentStep = null) => {
  // Check for validation errors in the response
  // Handle both error.response.data.errors and error.response.data.data.errors
  // The API may return errors in different structures:
  // 1. error.response.data.errors (direct)
  // 2. error.response.data.data.errors (wrapped in data object)
  let errorData = error?.response?.data;
  let validationErrors = null;

  // Check if errors are in data.data.errors first (wrapped response)
  // Then check data.errors (direct response)
  if (errorData?.data?.errors && typeof errorData.data.errors === "object") {
    validationErrors = errorData.data.errors;
  } else if (errorData?.errors && typeof errorData.errors === "object") {
    validationErrors = errorData.errors;
  }

  if (validationErrors) {
    const formikErrors = {};
    let hasFormikErrors = false;
    let stepToNavigate = null;

    // Process each validation error
    Object.entries(validationErrors).forEach(([backendField, messages]) => {
      const formikFieldPath = mapBackendFieldToFormik(backendField);
      const errorMessage = Array.isArray(messages) ? messages[0] : messages;

      // Show toast notification
      toast.error(`${formatFieldName(backendField)}: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
      });

      // Set Formik field error if formik instance is available and field is mapped
      if (formik && formikFieldPath) {
        const fieldParts = formikFieldPath.split(".");
        if (fieldParts.length === 2) {
          const [step, field] = fieldParts;
          if (!formikErrors[step]) {
            formikErrors[step] = {};
          }
          formikErrors[step][field] = errorMessage;
          hasFormikErrors = true;

          // Determine which step to navigate to
          if (step === "step1" && stepToNavigate !== 1) {
            stepToNavigate = 1;
          } else if (step === "step2" && stepToNavigate === null) {
            stepToNavigate = 2;
          }
        }
      }
    });

    // Set all Formik errors at once
    if (hasFormikErrors && formik) {
      formik.setErrors({
        ...formik.errors,
        ...formikErrors,
      });

      // Navigate to the appropriate step if needed
      if (setCurrentStep && stepToNavigate) {
        setCurrentStep(stepToNavigate);
      }

      // Mark fields as touched so errors are visible
      Object.keys(validationErrors).forEach((backendField) => {
        const formikFieldPath = mapBackendFieldToFormik(backendField);
        if (formikFieldPath) {
          formik.setFieldTouched(formikFieldPath, true);
        }
      });
    }

    return true;
  }

  // Handle other error messages
  if (errorData?.message || errorData?.reason) {
    const errorMessage = errorData.message || errorData.reason;
    if (errorMessage.includes("(and")) {
      const errorParts = errorMessage.split(".");
      errorParts.forEach((part) => {
        if (part.trim()) {
          toast.error(part.trim(), {
            position: "top-right",
            autoClose: 5000,
          });
        }
      });
    } else {
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    }
    return true;
  }

  // Fallback error message
  toast.error(
    error.message ||
      "Registration failed. Please check your information and try again.",
    {
      position: "top-right",
      autoClose: 5000,
    }
  );
  return false;
};

// Helper to format backend field names for better readability
const formatFieldName = (fieldName) => {
  const fieldNameMap = {
    brand_name: "Company Name",
    phone_number: "Phone Number",
    brand_category: "Industry",
    password_confirmation: "Password Confirmation",
  };

  if (fieldNameMap[fieldName]) {
    return fieldNameMap[fieldName];
  }

  return fieldName
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

function Signup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // *STATE HOOKS
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [otherLocation, setOtherLocation] = useState("");
  const [otherCategory, setOtherCategory] = useState("");
  const formikRef = useRef(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);
        const response = await GuestRequests.getCategories();
        console.log("category_response", response.data.data);
        if (response.data?.status_code === "000") {
          const categoryNames = response?.data?.data?.map(
            (category) => category.name
          );
          setCategoryList(categoryNames);
        } else {
          toast.error(response.data?.reason || "Categories could not fetched");
        }
      } catch (error) {
        toast.error(error.message);
        console.log(error);
      } finally {
        setCategoryLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const registerMutation = useMutation(
    async (formData) => {
      const response = await registerUser(formData);
      return response;
    },
    {
      onSuccess: (data) => {
        console.log("Registration success response:", data);

        // Handle validation errors that come through as successful HTTP response
        if (data?.data?.status_code === "004" && data?.data?.errors) {
          // This is a validation error, handle it like an error
          const mockError = {
            response: {
              data: data.data,
            },
          };
          handleServerErrors(mockError, formikRef.current, setCurrentStep);
          setIsLoading(false);
          return;
        }

        if (!data?.data || data?.data?.status_code === "999") {
          toast.error(data?.data?.reason || "Registration failed");
          setIsLoading(false);
          return;
        }

        // Extract the user data from the API response
        const userData = data?.data?.data;

        if (userData) {
          // Store user data in Redux
          dispatch(setUser(userData));
          dispatch(setRegistrationData(null));
          toast.success("Registration successful!");

          // Check if both verification fields are null
          const needsVerification =
            !userData.email_verified_at && !userData.phone_number_verified_at;
          console.log("Verification needed:", needsVerification);

          if (needsVerification) {
            console.log("Navigating to verify-otp");
            // Add a small delay to ensure Redux state is updated
            setTimeout(() => {
              navigate("/verify-otp");
            }, 100);
          } else {
            console.log("Navigating to dashboard");
            // Add a small delay to ensure Redux state is updated
            setTimeout(() => {
              navigate("/dashboard");
            }, 100);
          }
        } else {
          toast.error("Invalid response format");
          setIsLoading(false);
        }
      },
      onError: (error) => {
        handleServerErrors(error, formikRef.current, setCurrentStep);
      },
    }
  );

  // Add this helper function to show all form errors as individual notifications
  const showFormErrors = (errors) => {
    // Flatten nested errors structure from step1 and step2
    const flattenedErrors = {};

    if (errors.step1) {
      Object.entries(errors.step1).forEach(([field, error]) => {
        flattenedErrors[field] = error;
      });
    }

    if (errors.step2) {
      Object.entries(errors.step2).forEach(([field, error]) => {
        flattenedErrors[field] = error;
      });
    }

    // Show each error as an individual toast notification
    Object.entries(flattenedErrors).forEach(([field, error]) => {
      toast.error(`${formatFieldName(field)}: ${error}`, {
        position: "top-right",
        autoClose: 5000,
      });
    });
  };

  const renderStep1 = (formik) => (
    <motion.div
      key="step1"
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Field
            name="step1.brandName"
            as={TextField}
            label="Company Name"
            variant="outlined"
            fullWidth
            sx={textFieldStyles}
          />
          <ErrorMessage
            name="step1.brandName"
            component="div"
            className="mt-1 text-sm text-red-500"
          />
        </div>

        <div>
          <Field
            name="step1.email"
            as={TextField}
            label="Email"
            variant="outlined"
            fullWidth
            sx={textFieldStyles}
          />
          <ErrorMessage
            name="step1.email"
            component="div"
            className="mt-1 text-sm text-red-500"
          />
        </div>

        <div>
          <PhoneInput
            country={"gh"}
            value={formik.values.step1.phoneNumber}
            onChange={(phone) =>
              formik.setFieldValue("step1.phoneNumber", phone)
            }
            onBlur={() => formik.setFieldTouched("step1.phoneNumber", true)}
            inputStyle={{
              width: "100%",
              height: "56px",
              fontSize: "1rem",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              backgroundColor: "white",
              transition: "all 0.2s ease-in-out",
            }}
            buttonStyle={{
              borderRadius: "12px 0 0 12px",
              border: "1px solid #e2e8f0",
              borderRight: "none",
              backgroundColor: "white",
            }}
            containerStyle={{
              width: "100%",
            }}
            dropdownStyle={{
              width: "300px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
            inputClass="focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] hover:transform hover:-translate-y-0.5 hover:shadow-md"
            specialLabel=""
            enableSearch
            searchPlaceholder="Search country..."
          />
          <ErrorMessage
            name="step1.phoneNumber"
            component="div"
            className="mt-1 text-sm text-red-500"
          />
        </div>

        <div className="space-y-2">
          <FormControl fullWidth>
            <InputLabel
              id="category-label"
              sx={{
                "&.Mui-focused": {
                  color: "#FF6C6C",
                },
              }}
            >
              Industry
            </InputLabel>
            <Field
              name="step1.preferredCategory"
              as={Select}
              labelId="category-label"
              label="Industry"
              sx={selectStyles}
              onChange={(event) => {
                const value = event.target.value;
                formik.setFieldValue("step1.preferredCategory", value);
                if (value !== "Other") {
                  formik.setFieldValue("step1.customCategory", "");
                }
              }}
            >
              {[...categoryList, "Other"].map((category) => (
                <MenuItem
                  key={category}
                  value={category}
                  sx={{
                    "&:hover": {
                      backgroundColor:
                        category === "Other"
                          ? "rgba(255,108,108,0.1)"
                          : undefined,
                    },
                  }}
                >
                  {category === "Other" ? (
                    <div className="flex gap-2 items-center">
                      <motion.div
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5 text-[#FF6C6C]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </motion.div>
                      Add Custom Industry
                    </div>
                  ) : (
                    category
                  )}
                </MenuItem>
              ))}
            </Field>
            <ErrorMessage
              name="step1.preferredCategory"
              component="div"
              className="mt-1 text-sm text-red-500"
            />
          </FormControl>

          {formik.values.step1.preferredCategory === "Other" && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-4"
            >
              <Field name="step1.customCategory">
                {({ field, meta }) => (
                  <div className="relative">
                    <TextField
                      {...field}
                      label="Specify Your Industry"
                      variant="outlined"
                      fullWidth
                      sx={{
                        ...textFieldStyles,
                        "& .MuiOutlinedInput-root": {
                          ...textFieldStyles["& .MuiOutlinedInput-root"],
                          "&:before": {
                            content: '""',
                            position: "absolute",
                            top: "-16px",
                            left: "20px",
                            border: "8px solid transparent",
                            borderBottomColor: "rgba(255,108,108,0.1)",
                          },
                        },
                      }}
                      error={meta.touched && meta.error}
                      helperText={meta.touched && meta.error}
                      InputProps={{
                        endAdornment: field.value && (
                          <InputAdornment position="end">
                            <motion.button
                              type="button"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() =>
                                formik.setFieldValue("step1.customCategory", "")
                              }
                              className="p-1 text-gray-400 rounded-full hover:text-[#FF6C6C] hover:bg-[#FF6C6C]/10 focus:outline-none"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </motion.button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                )}
              </Field>
            </motion.div>
          )}
        </div>

        <div>
          <Field
            name="step1.password"
            as={TextField}
            type={showPassword ? "text" : "password"}
            label="Password"
            variant="outlined"
            fullWidth
            sx={textFieldStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowPassword(!showPassword)}
                    className="focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </motion.button>
                </InputAdornment>
              ),
            }}
          />
          <ErrorMessage
            name="step1.password"
            component="div"
            className="mt-1 text-sm text-red-500"
          />
          <PasswordStrengthIndicator password={formik.values.step1.password} />
        </div>

        <div>
          <Field
            name="step1.confirmPassword"
            as={TextField}
            type={showConfirmPassword ? "text" : "password"}
            label="Confirm Password"
            variant="outlined"
            fullWidth
            sx={textFieldStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Eye className="w-5 h-5 text-gray-400" />
                    )}
                  </motion.button>
                </InputAdornment>
              ),
            }}
          />
          <ErrorMessage
            name="step1.confirmPassword"
            component="div"
            className="mt-1 text-sm text-red-500"
          />
          <PasswordMatchIndicator
            password={formik.values.step1.password}
            confirmPassword={formik.values.step1.confirmPassword}
          />
        </div>
      </div>

      <motion.button
        type="button"
        onClick={() => {
          Object.keys(formik.values.step1).forEach((field) => {
            formik.setFieldTouched(`step1.${field}`, true);
          });

          formik.validateForm().then((errors) => {
            if (!errors.step1) {
              setCurrentStep(2);
            } else {
              // Show individual notifications for each error
              showFormErrors({ step1: errors.step1 });
            }
          });
        }}
        className={`w-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white font-semibold py-4 px-6 rounded-xl 
          ${
            Object.keys(formik.errors.step1 || {}).length > 0 || !formik.dirty
              ? "opacity-50 cursor-not-allowed"
              : "hover:scale-102 active:scale-98"
          }`}
        whileHover={
          Object.keys(formik.errors.step1 || {}).length === 0 && formik.dirty
            ? { scale: 1.02 }
            : {}
        }
        whileTap={
          Object.keys(formik.errors.step1 || {}).length === 0 && formik.dirty
            ? { scale: 0.98 }
            : {}
        }
        disabled={
          Object.keys(formik.errors.step1 || {}).length > 0 || !formik.dirty
        }
      >
        Next Step
      </motion.button>

      {/* Sign in link */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mt-4 text-center"
      >
        <p className="text-gray-600">
          Already have an account?{" "}
          <Link
            to="/signin"
            className="font-semibold text-[#FF6C6C] hover:text-[#ff8b8b] transition-colors"
          >
            Sign in
          </Link>
        </p>
      </motion.div>
    </motion.div>
  );

  const renderStep2 = (formik) => (
    <motion.div
      key="step2"
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormControl fullWidth>
          <InputLabel
            id="region-label"
            sx={{
              "&.Mui-focused": {
                color: "#FF6C6C",
              },
            }}
          >
            Region
          </InputLabel>
          <Field
            name="step2.region"
            as={Select}
            labelId="region-label"
            label="Region"
            sx={selectStyles}
            onChange={(event) => {
              const selectedRegion = event.target.value;
              formik.setFieldValue("step2.region", selectedRegion);
              // Reset location when region changes
              formik.setFieldValue("step2.location", "");
              formik.setFieldValue("step2.customLocation", "");
            }}
          >
            {regionOptions.map((region) => (
              <MenuItem key={region} value={region}>
                {region}
              </MenuItem>
            ))}
          </Field>
          <ErrorMessage
            name="step2.region"
            component="div"
            className="mt-1 text-sm text-red-500"
          />
        </FormControl>

        {/* City/Location Field */}
        <div className="space-y-4">
          <FormControl fullWidth>
            <InputLabel
              id="location-label"
              sx={{
                "&.Mui-focused": {
                  color: "#FF6C6C",
                },
              }}
            >
              City
            </InputLabel>
            <Field
              name="step2.location"
              as={Select}
              labelId="location-label"
              label="City"
              sx={selectStyles}
              onChange={(event) => {
                const selectedCity = event.target.value;
                formik.setFieldValue("step2.location", selectedCity);
                if (selectedCity !== "Other") {
                  formik.setFieldValue("step2.customLocation", "");
                }
              }}
              value={formik.values.step2.location}
              disabled={!formik.values.step2.region}
            >
              {!formik.values.step2.region ? (
                <MenuItem disabled value="">
                  <em>Please select a region first</em>
                </MenuItem>
              ) : (
                [...locationsByRegion[formik.values.step2.region], "Other"].map(
                  (city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  )
                )
              )}
            </Field>
            {!formik.values.step2.region && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-2 text-sm text-gray-500"
              >
                Select a region to view available cities
              </motion.p>
            )}
            <ErrorMessage
              name="step2.location"
              component="div"
              className="mt-1 text-sm text-red-500"
            />
          </FormControl>

          {/* Custom Location Input */}
          {formik.values.step2.location === "Other" && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Field name="step2.customLocation">
                {({ field }) => (
                  <TextField
                    {...field}
                    label="Enter City Name"
                    variant="outlined"
                    fullWidth
                    sx={textFieldStyles}
                    onChange={(e) => {
                      setOtherLocation(e.target.value);
                      formik.setFieldValue(
                        "step2.customLocation",
                        e.target.value
                      );
                      formik.setFieldValue("step2.location", "Other");
                    }}
                  />
                )}
              </Field>
              <ErrorMessage
                name="step2.customLocation"
                component="div"
                className="mt-1 text-sm text-red-500"
              />
            </motion.div>
          )}
        </div>
      </div>

      {/* Logo Upload Section */}
      <div className="p-6 bg-gray-50 rounded-xl border border-gray-300 border-dashed">
        <div className="space-y-4">
          <label className="block text-sm font-medium text-center text-gray-700">
            Brand Logo
          </label>
          <div className="flex flex-col justify-center items-center space-y-4">
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.currentTarget.files[0];
                formik.setFieldValue("step2.logo", file);
                setLogoPreview(URL.createObjectURL(file));
              }}
              className="hidden"
              id="logo-upload"
            />
            {!logoPreview ? (
              <label
                htmlFor="logo-upload"
                className="flex items-center justify-center px-6 py-3 space-x-2 text-sm font-medium text-white transition-all duration-200 rounded-lg cursor-pointer bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] hover:shadow-lg hover:-translate-y-0.5"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>Choose Logo</span>
              </label>
            ) : (
              <div className="relative">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="object-cover w-24 h-24 rounded-lg shadow-md"
                />
                <button
                  type="button"
                  onClick={() => {
                    setLogoPreview(null);
                    formik.setFieldValue("step2.logo", null);
                  }}
                  className="absolute -top-2 -right-2 p-1 text-white bg-red-500 rounded-full hover:bg-red-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}
            <p className="text-sm text-gray-500">
              Upload your brand logo (PNG, JPG up to 2MB)
            </p>
          </div>
        </div>
      </div>

      {/* Social Links Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-700">Social Links</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Website Link */}
          <div className="space-y-2">
            <Field name="step2.websiteLink">
              {({ field }) => (
                <TextField
                  {...field}
                  label="Website"
                  variant="outlined"
                  fullWidth
                  sx={textFieldStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Globe className="w-5 h-5 text-gray-400" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Field>
            <ErrorMessage
              name="step2.websiteLink"
              component="div"
              className="text-sm text-red-500"
            />
          </div>

          {/* Instagram Link */}
          <div className="space-y-2">
            <Field name="step2.instagramLink">
              {({ field }) => (
                <TextField
                  {...field}
                  label="Instagram"
                  variant="outlined"
                  fullWidth
                  sx={textFieldStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Instagram className="w-5 h-5 text-gray-400" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Field>
          </div>

          {/* Twitter/X Link */}
          <div className="space-y-2">
            <Field name="step2.twitterLink">
              {({ field }) => (
                <TextField
                  {...field}
                  label="X (Twitter)"
                  variant="outlined"
                  fullWidth
                  sx={textFieldStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <XIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Field>
          </div>

          {/* Facebook Link */}
          <div className="space-y-2">
            <Field name="step2.facebookLink">
              {({ field }) => (
                <TextField
                  {...field}
                  label="Facebook"
                  variant="outlined"
                  fullWidth
                  sx={textFieldStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Facebook className="w-5 h-5 text-gray-400" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Field>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex space-x-4">
        <motion.button
          type="button"
          onClick={() => setCurrentStep(1)}
          className="px-6 py-4 w-1/2 font-semibold text-gray-700 bg-gray-200 rounded-xl transition-all duration-200 hover:bg-gray-300"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Back
        </motion.button>

        <motion.button
          type="button"
          onClick={() => {
            Object.keys(formik.values.step2).forEach((field) => {
              formik.setFieldTouched(`step2.${field}`, true);
            });

            formik.validateForm().then((errors) => {
              if (!errors.step2) {
                setCurrentStep(3);
              } else {
                // Show individual notifications for each error
                showFormErrors({ step2: errors.step2 });
              }
            });
          }}
          className="w-1/2 bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white font-semibold py-4 px-6 rounded-xl"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Review Info
        </motion.button>
      </div>
    </motion.div>
  );

  const renderStep3 = (formik) => (
    <ReviewStep
      formData={formik.values}
      onBack={() => setCurrentStep(2)}
      onSubmit={() => {
        // Combine step1 and step2 values and handle custom values
        const submitData = {
          ...formik.values.step1,
          ...formik.values.step2,
          // Replace 'Other' with custom values if they exist
          preferredCategory:
            formik.values.step1.preferredCategory === "Other"
              ? formik.values.step1.customCategory
              : formik.values.step1.preferredCategory,
          location:
            formik.values.step2.location === "Other"
              ? formik.values.step2.customLocation
              : formik.values.step2.location,
        };
        delete submitData.customCategory;
        delete submitData.customLocation;

        dispatch(setRegistrationData(submitData));
        registerMutation.mutate(submitData);
      }}
      isLoading={registerMutation.isLoading}
    />
  );

  if (categoryLoading) {
    return <Loader />;
  }
  // Update the form render section
  return (
    <div className="flex flex-col w-full h-screen bg-gradient-to-br from-white to-gray-50 sm:flex-row">
      {/* Add ToastContainer for the notifications */}
      <ToastContainer position="top-right" autoClose={5000} limit={10} />

      <motion.div
        className="hidden relative w-full h-full sm:w-1/2 sm:flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <img
          src={Images.register_second}
          alt="dealboxx-signup"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30" />
        <div className="flex absolute inset-0 flex-col justify-between p-12">
          {/* Top Section */}
          <div className="space-y-8">
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                duration: 0.8,
                type: "spring",
                bounce: 0.4,
              }}
              className="space-y-2 text-white"
            >
              <motion.h3
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-light"
              >
                Become a Vendor on
              </motion.h3>
              <motion.h2
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-5xl font-bold bg-gradient-to-r from-[#FF6C6C] to-white bg-clip-text text-transparent"
              >
                DealBoxx
              </motion.h2>
            </motion.div>
          </div>

          {/* Middle Section - Stats */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.2,
                },
              },
            }}
            className="grid grid-cols-3 gap-6"
          >
            {[
              { number: "10k+", label: "Daily Visitors" },
              { number: "500+", label: "Active Vendors" },
              { number: "₵1M+", label: "Monthly Sales" },
            ].map((stat, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
                whileHover={{
                  scale: 1.05,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                }}
                className="p-6 text-center rounded-xl border backdrop-blur-sm transition-all duration-300 bg-white/5 border-white/10"
              >
                <motion.h4
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2 + index * 0.2 }}
                  className="mb-2 text-4xl font-bold text-white"
                >
                  {stat.number}
                </motion.h4>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2.2 + index * 0.2 }}
                  className="text-sm font-medium text-white/70"
                >
                  {stat.label}
                </motion.p>
              </motion.div>
            ))}
          </motion.div>

          {/* Bottom Section - Testimonial */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="p-8 bg-gradient-to-r to-transparent rounded-xl border backdrop-blur-md from-white/10 border-white/10"
            >
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="mb-6 text-xl italic font-medium leading-relaxed text-white/90"
              >
                "Join Ghana's fastest-growing marketplace and transform your
                business with DealBoxx's powerful vendor platform."
              </motion.p>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.4 }}
                className="flex gap-4 items-center"
              >
                <div className="flex -space-x-4">
                  {[1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1.4 + i * 0.2 }}
                      className="w-12 h-12 rounded-full border-2 backdrop-blur-sm border-white/20 bg-white/10"
                    />
                  ))}
                </div>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2 }}
                  className="text-sm font-medium text-white/80"
                >
                  Join <span className="text-[#FF6C6C] font-bold">1,000+</span>{" "}
                  successful vendors today
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {isLoading && <Loader />}
      {!isLoading && (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="overflow-y-auto px-8 pt-16 pb-8 w-full sm:w-1/2 sm:h-screen sm:px-16"
        >
          <Link to="/">
            <motion.div className="mb-6">
              <img
                src={Images.logo}
                alt="Dealboxx Logo"
                className="w-32 h-auto"
              />
            </motion.div>
          </Link>

          <motion.div className="mb-8 space-y-3">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
              Create Vendor Account
            </h1>
            <p className="text-[#808080] text-base">
              Join our community of vendors and start selling today.
            </p>
          </motion.div>

          <StepIndicator currentStep={currentStep} />

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              registerMutation.mutate(values, {
                onSettled: () => {
                  setSubmitting(false);
                },
              });
            }}
          >
            {(formik) => {
              // Store formik instance in ref for error handling
              formikRef.current = formik;
              
              return (
                <Form className="space-y-6">
                  {currentStep === 1
                    ? renderStep1(formik)
                    : currentStep === 2
                    ? renderStep2(formik)
                    : renderStep3(formik)}
                </Form>
              );
            }}
          </Formik>
        </motion.div>
      )}
    </div>
  );
}

// Add these styles
const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "white",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    },
    "&.Mui-focused": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(255,108,108,0.2)",
      "& fieldset": {
        borderColor: "#FF6C6C",
        borderWidth: "2px",
      },
    },
  },
  "& .MuiInputLabel-root": {
    "&.Mui-focused": {
      color: "#FF6C6C",
    },
  },
};

const selectStyles = {
  borderRadius: "12px",
  backgroundColor: "white",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#FF6C6C",
    borderWidth: "2px",
  },
  "& + .MuiFormLabel-root.Mui-focused": {
    color: "#FF6C6C",
  },
};

export default Signup;
